import React, { memo } from 'react'
import { Message } from '@paypalcorp/worldready-react'
import {
  Container,
  Row,
  Col,
  HeadingText,
  BodyText,
  Link,
  Divider,
} from '@paypalcorp/pp-react'
import { PrivacyHubLink, PrivacyHubModalLink } from './common'
import { homeConfig } from '../../config/privacy-hub'
import { func } from 'prop-types'
import serverData from '../../utils/server-data'

const PrivacyHubPrinciplesSection = memo(() => (
  <Container className="privacy-hub-principles-section-container">
    <Row noGutters>
      <Col xs={12} md={9} lg={8}>
        <HeadingText
          size="hero"
          className="privacy-hub-display-text"
          id="privacyComesFirst"
        >
          <Message id="privacyhub.home.privacyprinciples.title" />
        </HeadingText>
        <div className="privacy-hub-principles-content">
          <BodyText
            className="privacy-hub-body-text privacy-hub-principles-description"
            id="privacyPrinciplesBody"
          >
            <Message
              id="privacyhub.home.privacyprinciples.desc"
              link={(innerMessage) => (
                <Link
                  name="privacyHubPrinciples"
                  href="/myaccount/privacy/privacyhub/principles"
                  className="privacy-hub-hyper-link"
                  id="privacyPrinciplesLink"
                >
                  {innerMessage}
                </Link>
              )}
            />
          </BodyText>
        </div>
        <PrivacyHubLink
          name="privacyStatement"
          message="privacyhub.home.privacyprinciples.link"
          href="/webapps/mpp/ua/privacy-full"
          id="readOurPrivacyStmtLink"
        />
      </Col>
    </Row>
  </Container>
))

PrivacyHubPrinciplesSection.displayName = 'PrivacyHubPrinciplesSection'

const PrivacyHubMainLinkSection = memo(({ onSectionClick }) => (
  <Container className="privacy-hub-main-link-section-container">
    <Row noGutters>
      {homeConfig.mainLinks.map(
        ({
          id,
          name,
          icon,
          title,
          description,
          link,
          href = '',
          modal = '',
        }) => (
          <Col
            className="privacy-hub-main-link-item"
            xs={12}
            md={6}
            key={title}
          >
            <Container fluid>
              <Row noGutters>
                <Col
                  md={3}
                  className="privacy-hub-main-link-item-image-container"
                >
                  <img
                    className="privacy-hub-main-link-image"
                    src={icon.src}
                    alt={icon.alt}
                  />
                </Col>
                <Col md={9}>
                  <HeadingText
                    size="sm"
                    className="privacy-hub-title-text"
                    id={`${name}Title`}
                  >
                    <Message id={title} />
                  </HeadingText>
                  <BodyText
                    className="privacy-hub-body-text privacy-hub-main-link-item-description"
                    id={`${name}Desc`}
                  >
                    <Message id={description} />
                  </BodyText>
                  {href ? (
                    <PrivacyHubLink
                      message={link}
                      href={href}
                      name={name}
                      id={id}
                    />
                  ) : (
                    <PrivacyHubModalLink
                      id={id}
                      name={name}
                      message={link}
                      onSectionClick={() =>
                        onSectionClick({
                          name: modal,
                          title: (
                            <BodyText
                              className="privacy-hub-body-text"
                              id="manageYourDataTitle"
                            >
                              <Message id="privacyhub.privacyrights.modal.title" />
                            </BodyText>
                          ),
                        })
                      }
                    />
                  )}
                </Col>
              </Row>
            </Container>
          </Col>
        ),
      )}
    </Row>
  </Container>
))

PrivacyHubMainLinkSection.propTypes = {
  onSectionClick: func.isRequired,
}

PrivacyHubMainLinkSection.displayName = 'PrivacyHubMainLinkSection'

const PrivacyHubOthersAndDataSection = memo(() => (
  <Container className="privacy-hub-others-and-data-section-container">
    <Row noGutters className="privacy-hub-align-items-center">
      <Col
        xs={12}
        md={6}
        className="privacy-hub-others-and-data-image-container privacy-hub-text-align-center"
      >
        <img
          className="privacy-hub-others-and-data-image"
          src={`https://${serverData.cdnHostName}/privacyhub/${serverData.country}/${serverData.language}/lunch-illustration.svg`}
          alt="online-shopping"
        />
      </Col>
      <Col xs={12} md={6}>
        <Container fluid>
          {homeConfig.othersAndData.map(
            ({ id, key, title, description, link, href, name }, index) => (
              <div key={key}>
                <Row noGutters>
                  <Col>
                    <HeadingText
                      size="sm"
                      className="privacy-hub-title-text"
                      id={`${name}Title`}
                    >
                      <Message id={title} />
                    </HeadingText>
                    <BodyText
                      className="privacy-hub-body-text privacy-hub-others-and-data-description"
                      id={`${name}Desc`}
                    >
                      <Message id={description} />
                    </BodyText>
                    <PrivacyHubLink
                      message={link}
                      href={href}
                      name={name}
                      id={id}
                    />
                  </Col>
                </Row>
                {index !== homeConfig.othersAndData.length - 1 && (
                  <Row noGutters key={`divider-${index}`}>
                    <Col>
                      <div className="privacy-hub-others-and-data-divider-container">
                        <Divider />
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            ),
          )}
        </Container>
      </Col>
    </Row>
  </Container>
))

PrivacyHubOthersAndDataSection.displayName = 'PrivacyHubOthersAndDataSection'

const PrivacyHubBusinessSection = memo(() => (
  <Container className="privacy-hub-business-section-container">
    <Row noGutters className="privacy-hub-align-items-center">
      <Col xs={{ order: 2, span: 12 }} md={{ order: 1, span: 6 }}>
        <Container fluid>
          <Row noGutters>
            <Col>
              <div className="privacy-hub-business-content">
                <HeadingText
                  size="lg"
                  className="privacy-hub-large-text"
                  id="privacyhubHomeBusinessTitle"
                >
                  <Message id="privacyhub.home.business.title" />
                </HeadingText>
              </div>
              <BodyText
                className="privacy-hub-body-text privacy-hub-business-description"
                id="privacyhubHomeBusinessDesc"
              >
                <Message id="privacyhub.home.business.desc" />
              </BodyText>
              <PrivacyHubLink
                name="privacyFAQsForBusinesses"
                message="privacyhub.home.business.link"
                href="/myaccount/privacy/privacyhub/business"
                id="privacyhubHomeBusinessLink"
              />
            </Col>
          </Row>
        </Container>
      </Col>
      <Col
        xs={{ order: 1, span: 12 }}
        md={{ order: 2, span: 6 }}
        className="privacy-hub-business-image-container privacy-hub-text-align-center"
      >
        <img
          className="privacy-hub-business-image"
          src={`https://${serverData.cdnHostName}/privacyhub/private-business-illustration.svg`}
          alt="merchant"
        />
      </Col>
    </Row>
  </Container>
))

PrivacyHubBusinessSection.displayName = 'PrivacyHubBusinessSection'

const PrivacyHubHome = memo(({ onSectionClick }) => (
  <div id="privacy-hub-home">
    <PrivacyHubPrinciplesSection />
    <PrivacyHubMainLinkSection onSectionClick={onSectionClick} />
    <PrivacyHubOthersAndDataSection />
    <PrivacyHubBusinessSection />
  </div>
))

PrivacyHubHome.propTypes = {
  onSectionClick: func.isRequired,
}

PrivacyHubHome.displayName = 'PrivacyHubHome'

export default PrivacyHubHome
